import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthCtx = createContext(null);

const API_BASE = import.meta.env.VITE_API_URL || '';

export function AuthProvider({ children }) {
  const [recruiter, setRecruiter] = useState(null);
  const [token, setToken]         = useState(() => localStorage.getItem('jd_token'));
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API_BASE}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then(({ recruiter }) => setRecruiter(recruiter))
      .catch(() => { localStorage.removeItem('jd_token'); setToken(null); })
      .finally(() => setLoading(false));
  }, []);

  const persist = (tok, rec) => {
    localStorage.setItem('jd_token', tok);
    setToken(tok);
    setRecruiter(rec);
  };

  const login = useCallback(async (email, password) => {
    const r = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || 'Login failed');
    persist(data.token, data.recruiter);
  }, []);

  const register = useCallback(async (name, email, password, organization) => {
    const r = await fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, organization }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || 'Registration failed');
    persist(data.token, data.recruiter);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('jd_token');
    setToken(null);
    setRecruiter(null);
  }, []);

  const authFetch = useCallback((url, opts = {}) => {
    const { headers = {}, body, ...rest } = opts;
    const isFormData = body instanceof FormData;
    const finalHeaders = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
      Authorization: `Bearer ${token}`
    };
    return fetch(url, {
      ...rest,
      body,
      headers: finalHeaders,
    });
  }, [token]);

  return (
    <AuthCtx.Provider value={{ recruiter, token, loading, login, register, logout, authFetch }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
